import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Mail, CheckCircle, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Newsletter = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setStatus('loading');
    setErrorMessage('');

    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      setStatus('success');
      setEmail('');
    } catch (error) {
      setStatus('error');
      if (error.response?.data?.detail === 'Email already subscribed') {
        setErrorMessage('This email is already subscribed!');
      } else {
        setErrorMessage('Something went wrong. Please try again.');
      }
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="newsletter-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-cyan-500/10 to-blue-600/10 border border-cyan-500/20 p-8 md:p-12">
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-blue-600/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>

          <div className="relative z-10 max-w-2xl mx-auto text-center">
            <div className="w-14 h-14 rounded-2xl bg-cyan-500/20 flex items-center justify-center mx-auto mb-6">
              <Mail className="w-7 h-7 text-cyan-400" />
            </div>
            
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Stay Updated with Healthcare Tech
            </h2>
            <p className="text-slate-300 text-lg mb-8">
              Subscribe to our newsletter for the latest insights, innovations, and updates in healthcare technology.
            </p>

            <AnimatePresence mode="wait">
              {status === 'success' ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="flex items-center justify-center gap-3 text-emerald-400"
                >
                  <CheckCircle className="w-6 h-6" />
                  <span className="text-lg font-medium">Thank you for subscribing!</span>
                </motion.div>
              ) : (
                <motion.form
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onSubmit={handleSubmit}
                  className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto"
                >
                  <div className="flex-1">
                    <Input
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-12 bg-white/10 border-white/20 text-white placeholder:text-slate-400 rounded-full px-6 focus:border-cyan-400 focus:ring-cyan-400"
                      required
                      data-testid="newsletter-email-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={status === 'loading'}
                    className="h-12 px-8 btn-gradient text-white rounded-full font-semibold shadow-lg shadow-cyan-500/20"
                    data-testid="newsletter-submit-button"
                  >
                    {status === 'loading' ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      'Subscribe'
                    )}
                  </Button>
                </motion.form>
              )}
            </AnimatePresence>

            {status === 'error' && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-red-400 text-sm mt-4"
              >
                {errorMessage}
              </motion.p>
            )}

            <p className="text-slate-500 text-sm mt-6">
              We respect your privacy. Unsubscribe at any time.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Newsletter;
